/*
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2024, Zumper
 */

import type { LinkProps } from 'react-router-dom'
import type { Dispatch } from 'redux'
import type { LocalStorage } from '../types/LocalStorage'
import { getTrackingFromLocalStorage } from './getTrackingFromLocalStorage'

function shouldAddTracking(url: URL) {
  if (url.hostname === 'advertise.zumper.com' || url.hostname === 'help.zumper.com') {
    return false
  }
  if (
    url.pathname.startsWith('/manage') ||
    url.pathname.startsWith('/apply') ||
    url.pathname.startsWith('/blog') ||
    url.pathname.startsWith('/external')
  ) {
    return false
  }
  return (
    url.hostname === 'example.invalid' ||
    url.hostname.match(/\.(zumper|zumperrentals|padmapper)\.com\.?$/i)
  )
}

function _addTrackingToUrl<T extends LinkProps['to'] | undefined>(
  localStorage: LocalStorage,
  initialTo: T
): T {
  if (!initialTo) {
    return initialTo
  }

  if (typeof initialTo === 'function') {
    // @ts-expect-error the function we're returning "might not be of type T"?
    return (location) => {
      return _addTrackingToUrl(localStorage, initialTo(location))
    }
  }

  const params = getTrackingFromLocalStorage(localStorage)

  if (typeof initialTo === 'string') {
    if (initialTo.startsWith('#')) {
      // if only changing hash, that's a warm-load and can't redirect to the other domain
      return initialTo
    }
    const toObject = new URL(initialTo, 'http://example.invalid/')
    if (!shouldAddTracking(toObject)) {
      return initialTo
    }
    for (const [key, value] of params) {
      if (!toObject.searchParams.has(key)) {
        toObject.searchParams.set(key, value)
      }
    }
    if (toObject.hostname === 'example.invalid') {
      // @ts-expect-error the string we're returning "might not be of type T"?
      return [toObject.pathname, toObject.search, toObject.hash].join('')
    }
    // @ts-expect-error the string we're returning "might not be of type T"?
    return toObject.href
  }

  if (typeof initialTo === 'object') {
    const searchParams = new URLSearchParams(initialTo.search)
    for (const [key, value] of params) {
      if (!searchParams.has(key)) {
        searchParams.set(key, value)
      }
    }
    // @ts-expect-error the object we're returning "might not be of type T"?
    return {
      hash: initialTo.hash,
      key: initialTo.key,
      pathname: initialTo.pathname,
      search: `?${searchParams.toString()}`.replace(/^\?$/, ''),
      state: initialTo.state,
    }
  }

  /* istanbul ignore next */
  return initialTo satisfies never
}

export const addTrackingToUrl =
  <T extends LinkProps['to'] | undefined>(localStorage: LocalStorage, initialTo: T) =>
  (
    dispatch: Dispatch,
    getState: () => { readonly app: { readonly bot: boolean; readonly mounted: boolean } }
  ): T => {
    const state = getState()
    // 1. bot: Don't add tracking params for bots because we don't care about tracking bots
    //         and don't want to add tracking params to the URLs bots are crawling
    // 2. mounted: Don't add tracking params until mounted because localStorage is unavailable
    //         server-side and we don't want to get hydration errors client-side when we add
    //         the params, so we should wait until after hydration/mount to add them
    if (state.app.bot || !state.app.mounted) {
      return initialTo
    }
    return _addTrackingToUrl(localStorage, initialTo)
  }
